@if (thingId()) {
  <bxi-page [title]="titleDeviceType()" [breadcrumbOptions]="breadcrumbOptions()" [headerContent]="headerContent">
    <ng-template #headerContent>
      <div class="mt-[6px] flex grow items-start justify-between">
        <div class="gap-lg flex items-center">
          <bxi-status-badge [cardinality]="statusCardinality()"></bxi-status-badge>

          @let maintenanceName = maintenanceStatusName();
          @if (hasOngoingMaintenance()) {
            <bxi-state-badge [state]="maintenanceName" [label]="maintenanceLabel()" data-testid="maintenanceBadge"></bxi-state-badge>
          }

          @if (muting(); as muting) {
            <bx-badge
              size="lg"
              type="badge-color"
              icon="bell-off-03"
              iconType="icon-leading"
              color="gray"
              [label]="'deviceDetails.deviceStatus.muting.muted' | translate"
              [bxTooltip]="muting.reason"
              data-testid="mutedBadge"
            ></bx-badge>
          }
        </div>

        <div class="ml-lg gap-lg flex items-center">
          <button
            class="text-nowrap"
            bx-button
            size="lg"
            iconLeading="tool-02"
            type="secondary-gray"
            [disabled]="createMaintenanceOrderBlocked()"
            (click)="openCreateServiceOrderModal()"
          >
            {{ 'device.details.maintenance.order.create' | translate }}
          </button>
        </div>
      </div>
    </ng-template>

    <section class="gap-3xl flex w-full flex-col">
      <bxi-device-attributes></bxi-device-attributes>

      @if (isSrt()) {
        <bxi-srt-current-state></bxi-srt-current-state>
      }

      @if (isDraginoSensor()) {
        <bxi-ts-current-state></bxi-ts-current-state>
      }

      @if (isQundisSensor()) {
        <bxi-qtemp-current-state></bxi-qtemp-current-state>
      }

      @if (isPulseGasMeter()) {
        <bxi-pulse-gas-meter-current-state></bxi-pulse-gas-meter-current-state>
      }

      @if (isLorawanGateway()) {
        <bxi-lorawan-current-state></bxi-lorawan-current-state>
      }

      @if (isBasicMbusGateway()) {
        <bxi-basic-mbus-gateway-current-state></bxi-basic-mbus-gateway-current-state>
      }

      @if (isSubmeteringGateway()) {
        <bxi-submetering-gateway-current-state></bxi-submetering-gateway-current-state>
      }
    </section>

    <bx-tab-group
      class="mt-8"
      [activeIndex]="0"
      [size]="'md'"
      [type]="'underline'"
      [fullWidth]="false"
      (activeTabChange)="store.setActiveTabId($any($event.tabId))"
    >
      <!-- DEVICE STATUS -->
      @if (hasDeviceStatusTab()) {
        <ng-template #status>
          <span class="px-16">
            {{ 'deviceDetails.tab.status' | translate }}
          </span>
        </ng-template>
        <bx-tab [title]="status" class="p-4" [id]="tabIds.DEVICE_STATUS">
          <div class="mt-8 flex flex-col gap-8">
            @if (isSrt()) {
              <bxi-building-heating-disruption data-testid="buildingHeatingDisruptionPanel"></bxi-building-heating-disruption>
            }

            <bxi-device-status data-testid="deviceStatusPanel"></bxi-device-status>
          </div>
        </bx-tab>
      }

      <!-- DEVICE DATA -->
      <ng-template #data>
        <span class="px-16">
          {{ 'deviceDetails.tab.data' | translate }}
        </span>
      </ng-template>
      <bx-tab [title]="data" class="p-4" [id]="tabIds.DEVICE_DATA">
        @defer (when activeTabId() === tabIds.DEVICE_DATA) {
          <div class="gap-4xl mt-8 flex flex-col">
            @if (isSrt()) {
              <bxi-grafana-chart-card data-testid="timeseries-chart" [tabs]="srtChartTabs()"></bxi-grafana-chart-card>
            }

            @if (isDraginoSensor() || isQundisSensor()) {
              <bxi-grafana-chart-card data-testid="ts-timeseries-chart" [tabs]="tsChartTabs()"></bxi-grafana-chart-card>
            }

            @if (isPulseGasMeter()) {
              <bxi-grafana-chart-card data-testid="pulse-gas-meter-timeseries-chart" [tabs]="gasMeterChartTabs()"></bxi-grafana-chart-card>
            }

            @if (hasMaintenance(); as maintenance) {
              <bxi-maintenance-widget></bxi-maintenance-widget>
            }

            @if (isLorawanGateway()) {
              <bxi-lorawan-received-devices data-testid="receivedDevicesPanel"></bxi-lorawan-received-devices>
            }

            @if (isBasicMbusGateway()) {
              <bxi-basic-mbus-gateway-received-devices data-testid="mbusReceivedDevicesPanel"></bxi-basic-mbus-gateway-received-devices>
            }

            @if (isSubmeteringGateway()) {
              <bxi-submetering-gateway-received-devices
                data-testid="submeteringGatewayReceivedDevicesPanel"
              ></bxi-submetering-gateway-received-devices>
            }

            <bxi-device-raw-data></bxi-device-raw-data>

            @if (isSubmetering()) {
              <bxi-device-reception-history></bxi-device-reception-history>
            }
          </div>
        }
      </bx-tab>

      <!-- SETTINGS -->
      <ng-template #settings>
        <span class="px-16">
          {{ 'deviceDetails.tab.settings' | translate }}
        </span>
      </ng-template>
      @if (hasSettingsTab()) {
        <bx-tab [title]="settings" [id]="tabIds.FEATURES">
          @defer (when activeTabId() === tabIds.FEATURES) {
            @if (isSrt()) {
              <bxi-device-settings-panel class="mt-4xl">
                <bxi-srt-settings-essential></bxi-srt-settings-essential>
                <bxi-srt-settings-system></bxi-srt-settings-system>
                <bxi-srt-settings-resident-configuration></bxi-srt-settings-resident-configuration>
                <bxi-srt-settings-resident-operation></bxi-srt-settings-resident-operation>
              </bxi-device-settings-panel>
            } @else if (isDraginoSensor()) {
              <bxi-device-settings-panel class="mt-4xl">
                <bxi-ts-settings-essential></bxi-ts-settings-essential>
              </bxi-device-settings-panel>
            } @else {
              <bxi-device-settings-panel class="mt-4xl">
                <bxi-lorawan-settings-essential></bxi-lorawan-settings-essential>
              </bxi-device-settings-panel>
            }
          }
        </bx-tab>
      }

      <!-- CONTEXT -->
      <ng-template #context>
        <span class="px-16"> {{ 'deviceDetails.tab.context' | translate }} </span>
      </ng-template>
      <bx-tab [title]="context" [id]="tabIds.CONTEXT">
        @defer (when activeTabId() === tabIds.CONTEXT) {
          <bxi-device-context></bxi-device-context>
        }
      </bx-tab>
    </bx-tab-group>
  </bxi-page>
}
